import { useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, Send, Globe } from 'lucide-react'
import { ingestAPI } from '../../lib/api'

export function TextInput() {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!title.trim() || !content.trim()) {
      return
    }

    setIsSubmitting(true)
    
    try {
      await ingestAPI.uploadText(title, content, { is_public: isPublic })
      // Clear form
      setTitle('')
      setContent('')
      setIsPublic(false)
      // Show success message (assuming you have a toast system)
      alert('Text erfolgreich hinzugefügt!')
    } catch (error) {
      console.error('Error adding text:', error)
      alert('Fehler beim Hinzufügen des Textes')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl p-6"
    >
      <div className="flex items-center space-x-3 mb-6">
        <FileText className="w-6 h-6 text-primary-400" />
        <h2 className="text-xl font-bold">Text hinzufügen</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-2">
            Titel
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="z.B. Neue Richtlinie, FAQ Update..."
            required
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium mb-2">
            Inhalt
          </label>
          <textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-y"
            rows={8}
            placeholder="Fügen Sie hier den Text ein..."
            required
          />
          <p className="text-xs text-gray-400 mt-1">
            {content.length} Zeichen
          </p>
        </div>

        <div className="flex items-start space-x-3 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <input
            type="checkbox"
            id="isPublic"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="mt-1 w-4 h-4 text-primary-600 bg-white/10 border-white/20 rounded focus:ring-primary-500"
          />
          <label htmlFor="isPublic" className="cursor-pointer">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-blue-400" />
              <span className="font-medium">Dokument öffentlich machen</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Öffentliche Dokumente können von allen Nutzern eingesehen werden
            </p>
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !title.trim() || !content.trim()}
          className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Wird verarbeitet...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Text hinzufügen</span>
            </>
          )}
        </button>
      </form>
    </motion.div>
  )
}